/** @jsx jsx */
import React, { useEffect, useRef, useState } from 'react';
import { jsx } from '@compiled/react';
import flow from 'lodash/flow';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import DropdownMenu, { DropdownItemGroup, DropdownItem } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { token } from '@atlaskit/tokens';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import withFireUiAnalytics from '@atlassian/jira-analytics-web-react/src/components/with-fire-ui-analytics.tsx';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import type { User } from '@atlassian/jira-common-directory-v2/src/model/index.tsx';
import { injectIntl, useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import type { ReturnFocusTo } from '@atlassian/jira-software-modal-dialog/src/common/types.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import messages from './messages.tsx';
import { UpdateFilterModal } from './update-filter-modal/index.tsx';
import { CopyFilterModal } from './copy-filter-modal/index.tsx';

const styles = cssMap({
	container: {
		display: 'flex',
		justifyContent: 'flex-end',
		paddingTop: token('space.050'),
		paddingRight: token('space.025'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	spinnerContainer: {
		height: '16px',
		width: '24px',
	},
	dropdownWrapper: {
		paddingLeft: token('space.100'),
		paddingBlock: token('space.050'),
		paddingRight: token('space.150'),
	},
});

type Props = {
	isCopyFilterModalOpen: boolean;
	isWritable: boolean;
	defaultOpen: boolean;
} & {
	showSpinner: boolean;
	manageSubscriptionsUrl: string;
	atlassianAccountId: string;
	name: string | undefined;
	owner: User | null;
	isAdmin: boolean;
	filterId: string;
	onCopy: () => void;
	onChangeOwner: () => void;
	onEdit: () => void;
	onDelete: () => void;
	onEditFilterClick: (arg1: AnalyticsEvent) => void;
	onChangeOwnerFilterClick: (arg1: AnalyticsEvent) => void;
	onDeleteFilterClick: (arg1: AnalyticsEvent) => void;
	onManageSubscriptionsClick: (arg1: AnalyticsEvent) => void;
	onCopyFilterClick: (arg1: AnalyticsEvent) => void;
	onOpenCopyFilterModal: () => void;
	setReturnFocusTo: (returnFocusTo: ReturnFocusTo | null) => void | Promise<void>;
};

const DropdownMenuWithAnalytics = ComponentWithAnalytics('dropdownMenu', {
	onOpenChange: 'open',
})(DropdownMenu);

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

function Actions(props: Props) {
	const {
		isWritable = false,
		defaultOpen = false,
		showSpinner = false,
		manageSubscriptionsUrl = '',
		atlassianAccountId = '',
		name = undefined,
		owner = null,
		isAdmin = false,
		filterId = '',
		onCopy = noop,
		onChangeOwner = noop,
		onEdit = noop,
		onDelete = noop,
		onEditFilterClick = noop,
		onChangeOwnerFilterClick = noop,
		onDeleteFilterClick = noop,
		onManageSubscriptionsClick = noop,
		onCopyFilterClick = noop,
		onOpenCopyFilterModal = noop,
		setReturnFocusTo = noop,
	} = props;

	const { formatMessage } = useIntl();
	const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen);
	const [isCopyFilterModalOpen, setIsCopyFilterModalOpen] = useState(false);
	const triggerButtonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (props.isCopyFilterModalOpen && !isCopyFilterModalOpen) {
			setIsCopyFilterModalOpen(false);
			if (triggerButtonRef.current) {
				triggerButtonRef.current.focus();
			}
		}
	}, [props.isCopyFilterModalOpen, isCopyFilterModalOpen]);

	// @ts-expect-error - TS7031 - Binding element 'isOpen' implicitly has an 'any' type.
	const onOpenChange = ({ isOpen }) => {
		setIsDropdownOpen(isOpen);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleCopyFilterClick = (e: any, analytics: any) => {
		onCopyFilterClick(analytics);
		onCopy();
		onOpenCopyFilterModal();
		setIsCopyFilterModalOpen(true);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleManageSubscriptionsClick = (e: any, analytics: any) => {
		onManageSubscriptionsClick(analytics);
		delayedClose();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleEditFilterClick = (e: any, analytics: any) => {
		onEditFilterClick(analytics);
		onEdit();
		triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleChangeOwnerFilterClick = (e: any, analytics: any) => {
		onChangeOwnerFilterClick(analytics);
		onChangeOwner();
		if (fg('jfp_a11y_fix_filters_change_owner_modal_focus')) {
			triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
		}
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleDeleteFilterClick = (e: any, analytics: any) => {
		onDeleteFilterClick(analytics);
		onDelete();
		triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
	};

	/**
	 * Close the menu with a minimal delay so the inner handlers are able to react on keypress properly.
	 * In its current implementation if we close the menu right away it won't open the focused `href`.
	 * Ideally we have to get right of this and few more methods when the bug linked below is fixed.
	 * @see https://ecosystem.atlassian.net/browse/AK-4467
	 */
	const delayedClose = () => {
		setTimeout(() => setIsDropdownOpen(false));
	};

	const renderActions = () => {
		const isOwner = owner && owner.accountId === atlassianAccountId;

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const triggerFunction = ({ triggerRef, ...buttonProps }: { triggerRef: React.Ref<any> }) => (
			<IconButton
				{...buttonProps}
				appearance="subtle"
				icon={MoreIcon}
				ref={mergeRefs(triggerRef, triggerButtonRef)}
				isLoading={showSpinner}
				label={
					name ? formatMessage(messages.tooltipWithName, { name }) : formatMessage(messages.tooltip)
				}
				spacing="compact"
				isTooltipDisabled={false}
				tooltip={{
					content: formatMessage(messages.moreActionsTooltip),
					hideTooltipOnClick: true,
				}}
			/>
		);

		return (
			<Box xcss={styles.container}>
				<DropdownMenuWithAnalytics
					// @ts-expect-error - Type '{ children: Element; position: string; isOpen: boolean; onOpenChange: ({ isOpen }: { isOpen: any; }) => void; trigger: ({ triggerRef, ...props }: { [x: string]: any; triggerRef: any; }) => Element; }' is not assignable to type 'IntrinsicAttributes & Omit<{ appearance?: "default" | "tall" | undefined; autoFocus?: boolean | undefined; children?: ReactNode; isLoading?: boolean | undefined; ... 11 more ...; label?: string | undefined; }, keyof WithAnalyticsEventsProps>'.
					position="bottom right"
					isOpen={isDropdownOpen}
					onOpenChange={onOpenChange}
					trigger={triggerFunction}
					shouldRenderToParent
				>
					<DropdownItemGroup>
						<DropdownItemWithAnalytics
							href={manageSubscriptionsUrl}
							onClick={handleManageSubscriptionsClick}
						>
							{formatMessage(messages.manageAction)}
						</DropdownItemWithAnalytics>
						{fg('nin-save-filter-dialog-entry-point') ? (
							<CopyFilterModal filterId={filterId} returnFocusRef={triggerButtonRef} />
						) : (
							<DropdownItemWithAnalytics onClick={handleCopyFilterClick}>
								{formatMessage(messages.copyAction)}
							</DropdownItemWithAnalytics>
						)}
						{(isWritable || isOwner === true) &&
							(fg('nin-save-filter-dialog-entry-point') ? (
								<UpdateFilterModal filterId={filterId} returnFocusRef={triggerButtonRef} />
							) : (
								<DropdownItemWithAnalytics onClick={handleEditFilterClick}>
									{formatMessage(messages.editFilter)}
								</DropdownItemWithAnalytics>
							))}
						{(isOwner === true || isAdmin === true) && (
							<>
								<DropdownItemWithAnalytics onClick={handleChangeOwnerFilterClick}>
									{formatMessage(messages.changeOwner)}
								</DropdownItemWithAnalytics>
								<DropdownItemWithAnalytics onClick={handleDeleteFilterClick}>
									{formatMessage(messages.deleteFilter)}
								</DropdownItemWithAnalytics>
							</>
						)}
					</DropdownItemGroup>
				</DropdownMenuWithAnalytics>
			</Box>
		);
	};

	return (
		<Flex justifyContent="end" xcss={styles.dropdownWrapper}>
			{renderActions()}
		</Flex>
	);
}

const ActionsWithReturnToFocus = (props: Omit<Props, 'setReturnFocusTo'>) => {
	const { setReturnFocusTo } = useModalDialogActions();
	return <Actions {...props} setReturnFocusTo={setReturnFocusTo} />;
};

export default flow(
	injectIntl,
	withFireUiAnalytics({
		onManageSubscriptionsClick: 'manageSubscriptions',
		onCopyFilterClick: 'copy',
		onChangeOwnerFilterClick: 'changeOwner',
		onEditFilterClick: 'edit',
		onDeleteFilterClick: 'delete',
	}),
)(ActionsWithReturnToFocus);
